<script setup lang="ts">
/* ===========================================================================
   ui-delta — SideNav
   ---------------------------------------------------------------------------
   ADDS:      Left sub-navigation inside a feature area, collapsible to a 60px
              icon rail, with group headings, per-item counts and items kept
              at its foot (settings), in view while the page scrolls.
   UPSTREAM:  No equivalent in @ui. The products navigate by top nav plus tabs;
              a feature area with many grouped sub-sections needs a second axis.
   AT MERGE:  Move into @ui with RailLayout. Built entirely from the existing
              pill / raised-card vocabulary — it adds no tokens.
   ORIGIN:    elfie-design guide, components/navigation/SideNav.jsx.
   =========================================================================== */
import { computed, onMounted, ref, watch } from 'vue'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-vue'

export interface SideNavItem {
  name?: string
  label?: string
  /** Tabler icon component. Required in practice — see the warning below. */
  icon?: unknown
  count?: number
  /** Renders a group heading instead of a link; a divider rule when collapsed. */
  group?: string
  disabled?: boolean
  title?: string
}

interface Props {
  items?: SideNavItem[]
  /** Kept at the foot of the rail, apart from `items`, such as settings. Same shape, same `active`, same `change`. */
  footItems?: SideNavItem[]
  active?: string
  heading?: string
  collapsible?: boolean
  defaultCollapsed?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  footItems: () => [],
  active: undefined,
  heading: undefined,
  collapsible: false,
  defaultCollapsed: false,
})
const emit = defineEmits<{ change: [name: string] }>()

const collapsed = ref(props.defaultCollapsed)

/*
 * A collapsed rail is icons only, so an item without one falls back to its
 * initial — legible but generic. Warn loudly rather than let it pass: this is
 * the rule the collapsed rail depends on.
 */
const warnMissingIcons = (): void => {
  if (!props.collapsible) return
  const bad = [...props.items, ...props.footItems].filter((it) => !it.group && !it.icon).map((it) => it.label ?? it.name)
  if (bad.length === 0) return
  // eslint-disable-next-line no-console
  console.warn(
    `[Elfie SideNav] collapsible rail: these items have no icon, so they collapse to a bare initial — ${bad.join(', ')}.`,
  )
}
onMounted(warnMissingIcons)
watch(() => [props.items, props.footItems], warnMissingIcons, { deep: true })

const initial = (item: SideNavItem): string => (item.label ?? '').trim().charAt(0).toUpperCase()

const toggleLabel = computed(() => (collapsed.value ? 'Expand navigation' : 'Collapse navigation'))
</script>

<template>
  <nav :class="['rail', { 'rail--collapsed': collapsed, 'rail--with-foot': props.footItems.length > 0 }]">
    <div v-if="props.heading || props.collapsible" class="rail__head">
      <span v-if="!collapsed && props.heading" class="rail__heading">{{ props.heading }}</span>
      <button
        v-if="props.collapsible"
        type="button"
        class="rail__toggle"
        :aria-expanded="!collapsed"
        :aria-label="toggleLabel"
        :title="toggleLabel"
        @click="collapsed = !collapsed"
      >
        <component :is="collapsed ? IconChevronRight : IconChevronLeft" :size="16" />
      </button>
    </div>

    <template v-for="(item, index) in props.items" :key="item.name ?? `group-${index}`">
      <div v-if="item.group && collapsed" class="rail__rule" aria-hidden="true" />
      <div v-else-if="item.group" class="rail__group">{{ item.group }}</div>

      <button
        v-else
        type="button"
        :class="['rail__item', { 'rail__item--on': item.name === props.active }]"
        :disabled="item.disabled"
        :title="collapsed ? item.label : item.title"
        @click="item.name && emit('change', item.name)"
      >
        <component :is="item.icon" v-if="item.icon" :size="18" class="rail__glyph" />
        <span v-else class="rail__initial" aria-hidden="true">{{ initial(item) }}</span>
        <span v-if="!collapsed" class="rail__label">{{ item.label }}</span>
        <span v-if="!collapsed && item.count != null" class="rail__count">{{ item.count }}</span>
      </button>
    </template>

    <div v-if="props.footItems.length > 0" class="rail__foot">
      <button
        v-for="item in props.footItems"
        :key="item.name"
        type="button"
        :class="['rail__item', { 'rail__item--on': item.name === props.active }]"
        :disabled="item.disabled"
        :title="collapsed ? item.label : item.title"
        @click="item.name && emit('change', item.name)"
      >
        <component :is="item.icon" v-if="item.icon" :size="18" class="rail__glyph" />
        <span v-else class="rail__initial" aria-hidden="true">{{ initial(item) }}</span>
        <span v-if="!collapsed" class="rail__label">{{ item.label }}</span>
      </button>
    </div>
  </nav>
</template>

<style scoped>
.rail {
  width: 15.5rem;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  /* stylelint-disable-next-line declaration-property-unit-disallowed-list --
     A hairline gap between rail rows. The spacing ramp starts at 2px worth of
     nothing; --spacing-xxs (2px) would double it. Verbatim from the guide. */
  gap: 2px;
  transition: width 0.2s ease-in-out;
  font-family: var(--font-family-primary);
}

.rail--collapsed {
  width: 3.75rem;
}

.rail__head {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
  min-height: 2rem;
  justify-content: space-between;
  padding: 0 var(--spacing-xs) 0 var(--spacing-lg);
}

.rail--collapsed .rail__head {
  justify-content: center;
  padding: 0;
}

.rail__heading,
.rail__group {
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--fg-tertiary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rail__group {
  padding: 0 var(--spacing-lg);
  margin-top: var(--spacing-3xl);
  margin-bottom: var(--spacing-md);
}

.rail__rule {
  height: 1px;
  background: var(--line-subtle);
  margin: var(--spacing-lg) var(--spacing-xl) var(--spacing-md);
}

.rail__toggle {
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  border-radius: var(--radius-md);
  cursor: pointer;
  color: var(--fg-tertiary);
  transition:
    background-color 0.15s,
    color 0.15s;
}

.rail__toggle:hover {
  background: var(--surface-hover);
  color: var(--fg-primary);
}

/* The active item is the raised card on the recessed panel — the same
   figure/ground move the app bar makes with its white pill on the navy. */
.rail__item {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  width: 100%;
  box-sizing: border-box;
  justify-content: flex-start;
  text-align: left;
  padding: var(--spacing-md) var(--spacing-lg);
  border-radius: var(--radius-lg);
  border: 1px solid transparent;
  cursor: pointer;
  font-family: inherit;
  font-size: var(--font-size-body-2);
  line-height: var(--line-height-body-2);
  font-weight: var(--font-weight-regular);
  background: transparent;
  color: var(--fg-secondary);
  transition: background-color 0.2s;
}

.rail--collapsed .rail__item {
  gap: 0;
  justify-content: center;
  padding: var(--spacing-md) 0;
}

.rail__item:hover:not(:disabled) {
  background: var(--surface-hover);
}

.rail__item--on {
  font-weight: var(--font-weight-semibold);
  background: var(--surface-card);
  color: var(--fg-primary);
  /*
   * Definition comes from borders, not shadows. A white card on a recessed
   * panel is separated by a single hairline — without it the selected row is
   * near-invisible, which is exactly how it read before.
   */
  border: 1px solid var(--line-subtle);
  box-shadow: var(--shadow-xs);
}

.rail__item:disabled {
  cursor: not-allowed;
  color: var(--fg-disabled);
}

.rail__glyph {
  flex-shrink: 0;
  opacity: 0.6;
}

.rail__item--on .rail__glyph {
  opacity: 1;
}

.rail__initial {
  width: 1.125rem;
  height: 1.125rem;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-bold);
  color: var(--fg-tertiary);
}

.rail__label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rail__count {
  font-size: var(--font-size-body-3);
  font-weight: var(--font-weight-medium);
  color: var(--fg-tertiary);
  flex-shrink: 0;
}

/*
 * A rail with a foot runs the height of its RailLayout, so the foot sits at the
 * bottom of the panel, and stays in view at the bottom of the window while a
 * long page scrolls under it.
 */
.rail--with-foot {
  align-self: stretch;
}

.rail__foot {
  position: sticky;
  /* The panel's own end padding, if any, is not where its bottom is (theme.css). */
  bottom: calc(var(--spacing-xl) - var(--panel-end-padding, var(--spacing-none)));
  display: flex;
  flex-direction: column;
  gap: inherit;
  margin-top: auto;
  padding-top: var(--spacing-lg);
}

/* Below desktop the rail is already the space-saving form: a horizontal
   scroller. There is nothing left to collapse, so the toggle is hidden. */
@media (max-width: 1023px) {
  .rail,
  .rail--collapsed {
    width: 100%;
    flex-direction: row;
    align-items: center;
    gap: var(--spacing-xs);
    overflow-x: auto;
    scrollbar-width: none;
    /* stylelint-disable-next-line declaration-property-unit-disallowed-list --
       Room for the horizontal scroller's focus ring, not layout spacing. */
    padding-bottom: 2px;
  }

  .rail__head,
  .rail__rule,
  .rail__group {
    display: none;
  }

  /* In the scroller the foot is the last of its items. */
  .rail__foot {
    position: static;
    flex-direction: row;
    margin-top: 0;
    padding-top: 0;
  }

  .rail__item {
    width: auto;
    flex-shrink: 0;
    white-space: nowrap;
    padding: var(--spacing-md) var(--spacing-xl);
    border-radius: var(--radius-full);
    border: 1px solid transparent;
  }

  .rail__item--on {
    border-color: var(--line-strong);
  }
}
</style>
